import { CONTRACT_TRADING } from '@/constans/trade';
import { HsPairInfos__factory } from '@/contracts';
import { CONSTANT } from '@/store/constants';
import { sharedProvider } from '@/store/provider';
import dayjs from 'dayjs';
import { formatEther } from 'ethers';
import useSWR from 'swr';
import utc from 'dayjs/plugin/utc';

dayjs.extend(utc);

interface FeeData {
    fundingFee: string;
    rolloverFee: string;
    updatedAt: string;
}

const FEE_CHECKPOINT_HOURS = 4;

// Phí funding/rollover on-chain được chốt theo chu kỳ 4h kể từ createdAt,
// nên updatedAt phải là mốc checkpoint gần nhất tính từ createdAt, không phải thời điểm fetch.
function lastFeeCheckpoint(createdAt: string | number) {
    const created = dayjs.utc(Number(createdAt) * 1000);
    const elapsedHours = dayjs.utc().diff(created, 'hour');
    const checkpoints =
        Math.floor(elapsedHours / FEE_CHECKPOINT_HOURS) * FEE_CHECKPOINT_HOURS;

    return created.add(checkpoints, 'hour');
}

async function call({
    trader,
    pairIndex,
    index,
    createdAt,
    long,
    collateral,
    leverage,
}: {
    trader: string;
    pairIndex: number;
    index: number;
    createdAt: string | number;
    long: boolean;
    collateral: string;
    leverage: number;
}): Promise<FeeData> {
    const provider = sharedProvider.get(CONSTANT.RPC_WLD);
    const contract = HsPairInfos__factory.connect(
        CONTRACT_TRADING.PAIR_INFOS,
        provider
    );

    const [fundingFee, rolloverFee] = await Promise.all([
        contract.getTradeFundingFee(
            trader,
            pairIndex,
            index,
            long,
            collateral,
            leverage
        ),
        contract.getTradeRolloverFee(trader, pairIndex, index, collateral),
    ]);
    return {
        // fundingFee âm nghĩa là trader được cộng phí (không phải khoản phải trả) — không hiện âm trên UI
        fundingFee: formatEther(fundingFee < 0n ? 0n : fundingFee),
        rolloverFee: formatEther(rolloverFee),
        updatedAt: lastFeeCheckpoint(createdAt).toISOString(),
    };
}

export function useGetFees(
    trader: string,
    pairIndex: number,
    index: number,
    createdAt: string | number,
    long: boolean,
    collateral: string,
    leverage: number
) {
    const shouldFetch =
        !!trader && pairIndex !== undefined && index !== undefined;

    // Checkpoint hiện tại nằm trong SWR key: trong cùng 1 khung 4h, key không đổi
    // nên SWR chỉ dùng cache client, không gọi lại contract. Khi sang khung 4h mới,
    // key đổi -> tự fetch lại đúng 1 lần.
    const checkpoint = lastFeeCheckpoint(createdAt).valueOf();

    return useSWR<FeeData>(
        shouldFetch
            ? {
                  key: `useGetFees-${trader}-${pairIndex}-${index}-${createdAt}-${checkpoint}`,
                  trader,
                  pairIndex,
                  index,
                  createdAt,
                  long,
                  collateral,
                  leverage,
              }
            : null,
        call,
        {
            revalidateIfStale: false, // ❌ không tự refetch khi remount nếu cache trong khung 4h vẫn còn
            revalidateOnFocus: false, // ❌ không fetch khi focus lại tab
            revalidateOnReconnect: false, // ❌ không fetch khi mạng reconnect
            fallbackData: {
                fundingFee: '0',
                rolloverFee: '0',
                updatedAt: dayjs(Number(createdAt) * 1000)
                    .utc()
                    .toISOString(),
            },
        }
    );
}